// @ts-nocheck
import { IWindowCartItem } from '../types';
import { IQuestion } from '../types';
import { evaluateAllSurgePricesFromMessages } from './pricingCalculator';
import { calculatePriceFromMessages } from './configurationPriceCalculation';
import { MESSAGE_TYPE_ENUM, FOIL_SURCHARGE_RESOLVER } from '../constants';

const FOIL_RESOLVER = FOIL_SURCHARGE_RESOLVER;

const hasFoilCharge = (item: IWindowCartItem): boolean =>
  item.messages.some(
    m =>
      m.type === MESSAGE_TYPE_ENUM.priceSurge &&
      (m.surgeItems ?? []).some((s: any) => s.resolverValue === FOIL_RESOLVER),
  );

/**
 * Ensures the Specials Foil Surcharge is applied to exactly ONE window in the
 * cart — the first eligible one (based on pricing-rule evaluation).
 *
 * Called after any cart-item save so the invariant is maintained automatically:
 * - If the saved item lost the charge but another window qualifies → reassign.
 * - If multiple windows somehow carry it → keep only the first eligible one.
 * - If no window qualifies → charge is absent everywhere (correct).
 *
 * Only modifies items whose foil-charge status needs to change; everything else
 * is returned as-is so renders are minimal.
 */
export function reconcileFoilCharge(
  cartItems: IWindowCartItem[],
  questionFlow: IQuestion[],
  formatPrice: (amount: number) => string,
  marginPercentage = 0,
): IWindowCartItem[] {
  // Evaluate eligibility for every window
  const evaluated = cartItems.map(item => {
    const base = item.messages.filter(m => m.type !== MESSAGE_TYPE_ENUM.priceSurge);
    const surges = evaluateAllSurgePricesFromMessages(base, questionFlow, formatPrice, marginPercentage);
    const eligible = surges.some(
      m => (m.surgeItems ?? []).some((s: any) => s.resolverValue === FOIL_RESOLVER),
    );
    return { item, base, surges, eligible };
  });


  const eligibleIndices = evaluated.map((e, i) => (e.eligible ? i : -1)).filter(i => i >= 0);

  if (eligibleIndices.length === 0) {
    // Nothing qualifies — strip foil from any window that somehow has it
    return cartItems.map(item => {
      if (!hasFoilCharge(item)) return item;
      return stripFoil(item);
    });
  }

  // Current owner: first window that currently carries the foil charge
  const currentOwnerIdx = cartItems.findIndex(hasFoilCharge);

  // Target owner: keep current owner if still eligible, else use first eligible
  const targetOwnerIdx =
    currentOwnerIdx >= 0 && evaluated[currentOwnerIdx].eligible
      ? currentOwnerIdx
      : eligibleIndices[0];

  return evaluated.map(({ item, base, surges, eligible }, idx) => {
    const shouldHave = idx === targetOwnerIdx;
    const currently = hasFoilCharge(item);

    if (shouldHave === currently) return item; // already correct

    if (shouldHave) {
      // Add foil: replace all surges with fresh evaluation (includes foil)
      const msgs = [...base, ...surges];
      return { ...item, messages: msgs, price: calculatePriceFromMessages(msgs) };
    } else {
      // Strip foil
      return stripFoil(item);
    }
  });
}

function stripFoil(item: IWindowCartItem): IWindowCartItem {
  const msgs = item.messages
    .map(m =>
      m.type === MESSAGE_TYPE_ENUM.priceSurge
        ? { ...m, surgeItems: (m.surgeItems ?? []).filter((s: any) => s.resolverValue !== FOIL_RESOLVER) }
        : m,
    )
    .filter(m => m.type !== MESSAGE_TYPE_ENUM.priceSurge || (m.surgeItems ?? []).length > 0);
  return { ...item, messages: msgs, price: calculatePriceFromMessages(msgs) };
}
